<template>
    <div class="container-fluid p-user-invoices overflow-hidden">
        <div class="row justify-content-center">
            <div class="col-md-12">
                <div class="card">
                    <div class="card-header">お支払い一覧</div>

                    <div class="card-body p-invoices__content pt-2">
                        <div :class="{ 'u-loader u-loader--p5' : ! invoices }">
                            <div v-if="invoices" class="mt-2">
                                <b-table
                                    hover
                                    striped
                                    responsive
                                    :items="invoices"
                                    :fields="fields"
                                    :sort-by.sync="$store.state.userInvoices.sortBy"
                                    :sort-desc.sync="$store.state.userInvoices.sortDesc"
                                    @sort-changed="fetchInvoices"
                                    :tbody-tr-class="rowClass"
                                    class="table-bordered m-table vc-type-user">
                                    <template v-slot:cell(id)="data">
                                        {{ data.item.id }}
                                    </template>

                                    <template v-slot:cell(user_id)="data">
                                        {{ data.item.user_id }}
                                    </template>

                                    <template v-slot:cell(finalized_at)="data">
                                        <span v-if="data.item.finalized_at">{{ helper.dateTimeForHuman(data.item.finalized_at , timezone) }}</span>
                                    </template>

                                    <template v-slot:cell(total)="data">
                                        <span>{{ data.item.total.toLocaleString() }}</span><!--
                                        --><span class="small">&nbsp;円</span>
                                    </template>

                                    <template v-slot:cell(content)="data">
                                        <div class="content pb-2">
                                            <div>{{ data.item.content || `医療${$locale.consultation_host}料` }}</div>
                                            <div v-if="data.item.consultation_id !== null" class="small text-muted">{{ `${$locale.consultation_host}ID: #${data.item.consultation_id}` }}</div>
                                            <div v-else-if="data.item.subscription_id !== null" class="small text-muted">{{ `サブスクリプションID: #${data.item.subscription_id}` }}</div>
                                        </div>
                                    </template>

                                    <template v-slot:cell(user_coupon)="data">
                                        <div
                                            v-if="data.item.user_coupon && data.item.user_coupon.discount > 0"
                                            class="user_coupon pb-2">
                                            <div>
                                                <span class="u-palt">{{ data.item.user_coupon.name }}</span>
                                                <span class="u-palt small text-muted">{{ `（発行番号: #${data.item.user_coupon.id}）` }}</span>
                                            </div>
                                            <div class="small text-muted">{{ discountMethod(data.item.user_coupon) }}</div>
                                        </div>
                                        <div v-else>-</div>
                                    </template>

                                    <template v-slot:cell(invoice_attachments)="data">
                                        <div
                                            v-if="data.item.invoice_attachments.length > 0"
                                            class="invoice_attachments d-flex">
                                            <a
                                                v-for="attachment in data.item.invoice_attachments"
                                                :key="`attachment_${attachment.id}`"
                                                :href="attachment.presigned_url"
                                                target="_blank">
                                                <span>{{ attachment.origin_name }}</span>
                                            </a>
                                        </div>
                                        <div v-else>-</div>
                                    </template>

                                    <template v-slot:cell(payment_method_id)="data">
                                        <span v-if="data.item.payment_method && !canSkipPayment(data.item)">
                                            {{ data.item.payment_method.name }}
                                        </span>
                                        <span v-else>-</span>
                                    </template>

                                    <template v-slot:cell(status)="data">
                                        <div class="status">
                                            <span :class="{ 'is-invalid' : isFailed(data.item.status) }">
                                                {{ detectStatusName(data.item) }}
                                            </span>
                                        </div>
                                    </template>

                                    <template v-slot:cell(invoice_detail)="data">
                                        <div class="d-flex justify-content-center align-items-center">
                                            <b-button
                                                v-if="canRetryPayment(data.item)"
                                                type="button"
                                                class="btn btn-sm mr-2"
                                                variant="yokumiru-dark-green"
                                                @click="openModalRetryPayment(data.item)">
                                                お支払い
                                            </b-button>
                                            <b-button
                                                v-if="isAttempted(data.item.status) && !isInvoiceCompany(data.item)"
                                                type="button"
                                                class="btn btn-sm"
                                                variant="yokumiru-blue"
                                                target="_blank"
                                                :href="data.item.hosted_invoice_url">
                                                ダウンロード
                                            </b-button>
                                        </div>
                                    </template>
                                </b-table>

                                <div class="d-flex flex-wrap justify-content-between align-items-center">
                                    <b-pagination
                                          :total-rows="paginationConf.totalRows"
                                          :per-page="paginationConf.perPage"
                                          v-model="paginationConf.currentPage"
                                          @change="fetchInvoices">
                                    </b-pagination>

                                    <div class="d-flex flex-wrap align-items-center">
                                        <div class="mr-3 mb-3">
                                            <span class="small">{{ dispPaginationDetail }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <admin-user-profile-modal :timezone="timezone"/>
        <admin-doctor-profile-modal/>
        <user-profile-modal/>

        <cancel-invoice-modal/>
        <subscription-cancel-modal/>

        <retry-payment-modal
            :pubkey="pubkey"
            :timezone="timezone">
        </retry-payment-modal>
    </div>
</template>

<script>
    import constants from '../../constants'
    import moment from 'moment-timezone'
    import YokumiruHelper from '../../YokumiruHelper'
    import { mapMutations } from 'vuex'

    export default {
        name: 'Invoices',
        props: {
            timezone: {
                type: String,
                default: 'Asia/Tokyo',
            },
            pubkey: {
                type: String,
                required: true,
            },
            setup: {
                type: String,
                default: '',
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                inProgress: false,
                invoices: null,
                search: {
                    status: null,
                    plan_id: null,
                    freeword: '',
                    finalized_at_gte: null,
                    finalized_at_lte: null,
                },
                user_id: null,
                paginationConf: {
                    currentPage: 1,
                    perPage: 10,
                    totalRows: 0,
                    from: 0,
                    to: 0,
                },
                user_categories: [],
                sortBy: 'id',
                sortDesc: true,
                invoiceStatus: constants.invoices,
                fields: [],
            }
        },

        computed: {
            /**
             * ページング詳細表示
             * @returns {string}
             */
            dispPaginationDetail: function () {
                if ( this.$store.state.userInvoices.total < 1 ) {
                    return ''
                }
                return `全${this.$store.state.userInvoices.total}件中 ${this.$store.state.userInvoices.from}～${this.$store.state.userInvoices.to}件表示`
            },
        },

        created: function () {
            this.initQueryFromState()
            this.initFields()
            this.fetchInvoices()

            bus.$on('retry-payment-completed', this.updateInvoicePaid)
            bus.$on('subscription-canceled', this.fetchInvoices)
        },

        beforeDestroy: function () {
            bus.$off('retry-payment-completed')
            bus.$off('subscription-canceled')
        },

        mounted: function () {
            if (this.setup === '3d') {
                bus.$emit('alert-danger', '3Dセキュア認証が必要なカードです。「お支払い」ボタンから支払いを完了させてください。', 10000)
            }
        },

        methods: {
            ...mapMutations({
                setQuery: 'userInvoices/setQuery',
                setQueries: 'userInvoices/setQueries',
            }),

            /**
             * 検索条件の初期化
             */
            initQueryFromState: function () {
                if (localStorage.getItem('yokumiruWeb')) {
                    const data = JSON.parse(localStorage.getItem('yokumiruWeb'))
                    this.setQueries(data.userInvoices)
                }
            },

            rowClass: function (item, type) {
                if (item && type === 'row') {
                    if (this.isFailed(item.status)) {
                        return 'is-invalid'
                    }
                }
                return null
            },

            initFields: function () {
                this.fields = [
                    {
                        key: 'id',
                        label: '#',
                        sortable: true,
                    },
                    {
                        key: 'finalized_at',
                        label: '請求日時',
                        sortable: true,
                    },
                    {
                        key: 'content',
                        label: '請求内容',
                        sortable: false,
                    },
                    {
                        'key': 'total',
                        'label': '金額（税込）',
                        sortable: true,
                    },
                    {
                        key: 'payment_method_id',
                        label: 'お支払い方法',
                        sortable: true,
                    },
                    {
                        key: 'status',
                        label: 'ステータス',
                        sortable: true,
                    },
                    {
                        key: 'invoice_detail',
                        label: '',
                        class: 'text-center',
                        sortable: false,
                    },
                ]

                if (this.$store.state.configs.subscription == 1) {
                    this.fields.splice(3, 0, {
                        key: 'user_coupon',
                        label: 'クーポン',
                        sortable: false,
                    })
                }
                else {
                    this.fields.splice(6, 0, {
                        key: 'invoice_attachments',
                        label: '添付ファイル',
                        sortable: false,
                    })
                }
            },

            /**
             * 請求一覧
             *
             * @param {number|object} param
             */
            fetchInvoices: async function (param = null) {
                if (this.inProgress) return
                this.inProgress = true

                this.invoices = null

                if (Number.isInteger(param) && param > 0) {
                    await this.setQuery({key: 'current_page', value: param})
                }
                else if (param !== null && param.hasOwnProperty('sortBy')) {
                    await this.setQuery({key: 'sortBy', value: param.sortBy})
                    await this.setQuery({key: 'sortDesc', value: param.sortDesc})
                }

                const endpoint = constants.user.invoices.endpoint.base

                await axios.get(`${endpoint}${this.buildQuery()}`)
                    .then(({data}) => {
                        this.invoices = data.data
                        this.setQuery({key: 'current_page', value: data.meta.current_page})
                        this.setQuery({key: 'per_page', value: data.meta.per_page})
                        this.setQuery({key: 'total', value: data.meta.total})
                        this.setQuery({key: 'from', value: data.meta.from})
                        this.setQuery({key: 'to', value: data.meta.to})
                    })

                this.inProgress = false
            },

            /**
             * QueryString生成
             *
             * @param {boolean} paginate
             * @return {string}
             */
            buildQuery: function(paginate = true) {
                let query = '?'

                const state = this.$store.state.userInvoices

                if (paginate) {
                    query += `page=${state.current_page}`
                }
                query += `&sort=${state.sortBy}`
                query += `&desc=${state.sortDesc}`

                if (this.search.status) {
                    query += `&status=${this.search.status}`
                }
                if (this.search.plan_id) {
                    query += `&plan_id=${this.search.plan_id}`
                }
                if (this.search.freeword.length > 0) {
                    query += `&freeword=${this.search.freeword}`
                }
                if (this.search.finalized_at_gte && this.search.finalized_at_gte.length > 0) {
                    query += `&finalized_at_gte=${moment(this.search.finalized_at_gte).format('YYYY-MM-DD')}`
                }
                if (this.search.finalized_at_lte && this.search.finalized_at_lte.length > 0) {
                    query += `&finalized_at_lte=${moment(this.search.finalized_at_lte).format('YYYY-MM-DD')}`
                }

                return query
            },

            /**
             * 割引方式を返す
             * @param {object} userCoupon
             * @return {string}
             */
            discountMethod: function (userCoupon) {
                if (!userCoupon) return ''

                const discount = userCoupon.discount.toLocaleString()

                if (userCoupon.discount_unit === 'percent') {
                    return `${discount}% 割引`
                }
                else if (userCoupon.discount_unit === 'static') {
                    return `${discount}円 割引`
                }
                else if (userCoupon.discount_unit === 'daily') {
                    return `${discount}円/日 割引`
                }
                return ''
            },

            /**
             * 支払い再試行完了後のデータ更新
             *
             * @param {object} invoice
             */
            updateInvoicePaid: function (invoice) {
                const inv = this.invoices.find(inv => inv.id === invoice.id)

                if (inv) {
                    inv.status = constants.invoices.paid
                    inv.stripe_status = 'paid'
                }
            },

            /**
             * 支払い再試行モーダルの表示
             *
             * @param {object} invoice
             */
            openModalRetryPayment: function (invoice) {
                bus.$emit('open-modal-retry-payment', invoice)
            },

            /**
             * 支払い再試行ができる状態かどうか
             *
             * @param {object} invoice
             * @return {boolean}
             */
            canRetryPayment: function (invoice) {
                // ステータスが決済失敗ではない
                if (invoice.status !== this.invoiceStatus.failed) {
                    return false
                }
                // 紐付いたサブスクリプションがすでに削除されている
                if (invoice.subscription_id && invoice.subscription === null) {
                    return false
                }
                return true
            },

            /**
             * 無料かどうか
             *
             * @param {object} invoice
             * @return {boolean}
             */
            canSkipPayment: function (invoice) {
                return this.$locale.type === 'consul'
                    && invoice.consultation
                    && invoice.consultation.estimated_total <= 0
            },

            /**
             * ステータス表記
             *
             * @param {object} invoice
             * @return {string}
             */
            detectStatusName: function (invoice) {
                let result = this.invoiceStatus.all.find(status => {
                    return status.value === invoice.status
                })

                if (result && this.$store.state.configs.subscription != 1) {
                    if (result.value === constants.invoices.authorized
                        && invoice.send_at
                        && invoice.payment_method_id !== constants.payment_methods.credit) {
                        return '請求済'
                    }
                }

                return result ? result.text : ''
            },

            /**
             * 失敗ステータス判定
             *
             * @param {number} status
             * @return {boolean}
             */
            isFailed: function (status) {
                return status === this.invoiceStatus.failed
                    || status === this.invoiceStatus.authorizeFailed
            },

            /**
             * 決済試行済みステータス判定
             *
             * @param {number} status
             * @return {boolean}
             */
            isAttempted: function (status) {
                return status === this.invoiceStatus.paid
                    || status === this.invoiceStatus.failed
                    || status === this.invoiceStatus.refunded
            },

            /**
             * 法人会員の請求書判定
             *
             * @param {object} invoice
             * @return {boolean}
             */
            isInvoiceCompany: function (invoice) {
                return invoice.group && invoice.group.group_type === constants.groupType.company
            },
        },
    };
</script>

<style lang="scss">
.p-user-invoices {
    .b-table {
        min-width: 1200px;

        th, td {
            font-size: 0.75rem;
        }
    }
}
</style>

<style scoped lang="scss">
.content,
.user_coupon,
.invoice_attachments {
    overflow-x: scroll;
    white-space: pre;
    width: 240px;
    padding: 5px 0;
    box-sizing: border-box;
    line-height: 0.8;

    > a {
        display: block;
        padding: 4px 10px;
        color: #212529;
        text-align: center;
        background-color: #dedede;
        border-radius: 15px;
        margin-right: 5px;

        &:last-child {
            padding-right: 15px;
        }
    }
}
</style>
